import { dspAssetUrl } from './dspAssetUrl';
const WORKER_URL = dspAssetUrl('/audio/samplePreparationWorker.js');

let worker = null;
let nextJobId = 1;
const pending = new Map();

const failPending = (error) => {
  pending.forEach(({ reject }) => reject(error));
  pending.clear();
};

const getWorker = () => {
  if (worker) return worker;
  if (typeof globalThis.Worker !== 'function') return null;
  // A chat card/render document has a different origin from the engine files.
  // Browsers can reject Worker construction synchronously even when Worker
  // exists. Return a rejected preparation job so callers use their established
  // fallback; waveform preparation must never abort loading audible audio.
  try { worker = new globalThis.Worker(WORKER_URL); }
  catch (_) { worker = null; return null; }
  worker.onmessage = ({ data }) => {
    const job = pending.get(data?.id);
    if (!job) return;
    pending.delete(data.id);
    if (!data.ok) {
      job.reject(new Error(data.error || 'Sample preparation failed'));
      return;
    }
    if (data.peaks) {
      job.resolve({
        peaks: new Float32Array(data.peaks),
        length: data.length,
        sampleRate: data.sampleRate,
        bucketSize: data.bucketSize
      });
      return;
    }
    job.resolve({
      channels: (data.channels || []).map((buffer) => new Float32Array(buffer)),
      ratio: data.ratio,
      taps: data.taps,
      metrics: data.metrics || null
    });
  };
  worker.onerror = () => {
    failPending(new Error('Sample preparation worker failed'));
    worker?.terminate?.();
    worker = null;
  };
  return worker;
};

/**
 * Prepare pitch/sample-rate conversion away from the UI and audio threads.
 * Input channels are copied before transfer, so the source AudioBuffer remains
 * valid while the worker owns the job memory.
 */
const prepareChannels = (type, channels, ratio, { taps = 32 } = {}) => {
  const target = getWorker();
  if (!target) return Promise.reject(new Error('Sample preparation worker unavailable'));
  const id = nextJobId++;
  const copies = (channels || []).map((channel) => new Float32Array(channel));
  const buffers = copies.map((channel) => channel.buffer);
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject });
    target.postMessage({ id, type, channels: buffers, ratio, taps }, buffers);
  });
};

const prepareWithOptions = (type, channels, options = {}) => {
  const target = getWorker();
  if (!target) return Promise.reject(new Error('Sample preparation worker unavailable'));
  const id = nextJobId++;
  const copies = (channels || []).map((channel) => new Float32Array(channel));
  const buffers = copies.map((channel) => channel.buffer);
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject });
    target.postMessage({ id, type, channels: buffers, ...options }, buffers);
  });
};

export const resampleChannelsHighQuality = (channels, ratio, options) => (
  prepareChannels('resample', channels, ratio, options)
);

export const pitchShiftChannelsPreserveDuration = (channels, ratio, options) => (
  prepareChannels('pitchPreserveDuration', channels, ratio, options)
);

export const stretchChannelsPreservePitch = (channels, ratio, options) => (
  prepareChannels('tempoStretch', channels, ratio, options)
);

export const limitChannelsTruePeak = (channels, {
  sampleRate,
  ceilingDb = -1,
  lookaheadMs = 5,
  releaseMs = 100,
  taps = 32
} = {}) => prepareWithOptions('truePeakLimit', channels, {
  sampleRate, ceilingDb, lookaheadMs, releaseMs, taps
});

export const buildWaveformPeaks = (channels, {
  sampleRate = 48000,
  bucketSize = 256
} = {}) => prepareWithOptions('waveformPeaks', channels, { sampleRate, bucketSize });

/** Audible compatibility fallback when Worker/WASM is unavailable. This is a
 * linked, transparent safety trim—not advertised as true-peak limiting. */
export const limitChannelsSamplePeakFallback = (channels, ceilingDb = -1) => {
  const requestedCeiling = Number(ceilingDb);
  const ceiling = Math.pow(10, Math.max(
    -12,
    Math.min(0, Number.isFinite(requestedCeiling) ? requestedCeiling : -1)
  ) / 20);
  let peak = 0;
  (channels || []).forEach((channel) => {
    for (let index = 0; index < channel.length; index += 1) {
      peak = Math.max(peak, Math.abs(channel[index] || 0));
    }
  });
  const gain = peak > ceiling ? ceiling / peak : 1;
  return (channels || []).map((channel) => Float32Array.from(channel, (sample) => sample * gain));
};

export const resetSamplePreparationWorkerForTests = () => {
  worker?.terminate?.();
  worker = null;
  nextJobId = 1;
  failPending(new Error('Sample preparation worker reset'));
};
